<template>
  <v-card
    v-motion
    :initial="{ y: 30 }"
    :enter="{ y: 0 }"
    :class="compact ? 'bank-switcher--compact' : 'pa-6'"
    :elevation="compact ? 0 : undefined"
    :flat="compact"
  >
    <v-select
      :model-value="selectedProduct"
      :items="productOptions"
      item-title="text"
      item-value="value"
      :label="compact ? 'Product' : 'Select product'"
      variant="outlined"
      :density="compact ? 'compact' : 'default'"
      :hide-details="compact"
      :class="compact ? '' : 'mb-2'"
      @update:model-value="selectProduct"
      :item-props="
        (item) => ({
          disabled: item.soon,
          title: item.soon ? `${item.text} (Coming soon!)` : item.text,
        })
      "
    />
  </v-card>
</template>

<script setup>
  import { computed } from "vue";
  import { doc } from "firebase/firestore";
  import { useRoute, useRouter } from "vue-router";
  import { useCurrentUser, useDocument, useFirestore } from "vuefire";
  import { useAdminAccessStore } from "@/store/adminAccess";
  import { useBankStore } from "@/store/bank";
  import { usePaymentStore } from "@/store/payment";
  import { BANK_OPTIONS } from "@/constants/banks";

  defineProps({
    compact: {
      type: Boolean,
      default: false,
    },
  });

  const bankStore = useBankStore();
  const adminAccessStore = useAdminAccessStore();
  const paymentStore = usePaymentStore();
  const route = useRoute();
  const router = useRouter();
  const currentUser = useCurrentUser();
  const firestore = useFirestore();
  const pub2MembershipRef = computed(() =>
    currentUser.value
      ? doc(firestore, "pub2Memberships", currentUser.value.uid)
      : null,
  );
  const { data: pub2Membership } = useDocument(pub2MembershipRef);
  const isPub2Admin = computed(
    () =>
      adminAccessStore.isSuperAdmin ||
      (
        ["Question Writer", "Admin"].includes(adminAccessStore.level) &&
        adminAccessStore.canAccessBank("pub2")
      ),
  );
  const isPub2Visible = computed(
    () =>
      isPub2Admin.value ||
      Boolean(paymentStore.isSubscribed?.pub2?.val) ||
      pub2Membership.value?.active === true,
  );
  const productOptions = computed(() => [
    ...BANK_OPTIONS,
    ...(isPub2Visible.value
      ? [{
          text: "Pub2 Research Programme",
          value: "pub2",
          soon: false,
        }]
      : []),
  ]);
  const selectedProduct = computed(() =>
    route.path.startsWith("/pub2") ? "pub2" : bankStore.currentBank,
  );
  const selectProduct = async (value) => {
    if (value === "pub2") {
      await router.push("/pub2");
      return;
    }
    bankStore.updateBank(value);
    if (route.path.startsWith("/pub2")) {
      await router.push("/");
    }
  };
</script>

<style scoped>
  .bank-switcher--compact {
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    max-width: 340px;
    overflow: visible;
    width: 100%;
  }

  .bank-switcher--compact :deep(.v-field) {
    background: #fff;
    border-radius: 14px;
    box-shadow: none;
  }

  .bank-switcher--compact :deep(.v-field__outline) {
    --v-field-border-opacity: 1;
    color: var(--pub2-border);
  }

  @media (max-width: 800px) {
    .bank-switcher--compact {
      max-width: none;
      width: 100%;
    }
  }
</style>
